// Page3.js - Roof Analysis Results Display
'use strict';

import { logger, FIELDS } from '../utils/logger.js';
import { RoofFace } from '../utils/RoofFace.js';

/**
 * Centralized face selection state management
 * Single source of truth for all face selection operations
 */
class FaceSelectionManager {
  constructor(page3) {
    this.page3 = page3;
    this.observers = []; // For future extensibility
  }

  /**
   * Single entry point for ALL selection changes
   * @param {number} faceId - Face identifier
   * @param {boolean} selected - New selection state
   * @param {string} source - Source of the change (for debugging)
   * @returns {boolean} True if state changed, false if no change needed
   */
  setSelection(faceId, selected, source = 'unknown') {
    // 1. Validate
    if (!this.isValidFace(faceId)) {
      logger.error({
        [FIELDS.OPERATION]: 'setSelection',
        [FIELDS.PAGE_NUMBER]: 3,
        [FIELDS.REASON]: 'invalid_face_id',
        faceId: faceId,
        source: source
      }, `Invalid face ID: ${faceId}`);
      return false;
    }

    // 2. Update single source of truth
    const face = this.page3.facesData.find(f => f.id === faceId);
    if (!face) {
      logger.error({
        [FIELDS.OPERATION]: 'setSelection',
        [FIELDS.PAGE_NUMBER]: 3,
        [FIELDS.REASON]: 'face_not_found',
        faceId: faceId,
        source: source
      }, `Face not found: ${faceId}`);
      return false;
    }

    if (face.selected === selected) {
      return false; // No change needed
    }

    face.selected = selected;

    // 3. Trigger all UI updates in correct order
    this.updateAllViews(faceId, selected);

    // 4. Notify observers
    this.notifyObservers(faceId, selected, source);

    return true;
  }

  /**
   * Toggle face selection state
   * @param {number} faceId - Face identifier
   * @param {string} source - Source of the change
   * @returns {boolean} True if state changed
   */
  toggle(faceId, source = 'toggle') {
    const face = this.page3.facesData.find(f => f.id === faceId);
    if (!face) return false;

    return this.setSelection(faceId, !face.selected, source);
  }


  /**
   * Get currently selected faces
   * @returns {Array} Array of selected face data
   */
  getSelectedFaces() {
    return this.page3.facesData.filter(face => face.selected);
  }

  /**
   * Validate face ID
   * @param {number} faceId - Face identifier to validate
   * @returns {boolean} True if valid
   */
  isValidFace(faceId) {
    return Number.isInteger(faceId) &&
           faceId >= 0 &&
           faceId < this.page3.facesData.length;
  }

  /**
   * Atomic update of all UI components
   * @param {number} faceId - Face identifier
   * @param {boolean} selected - New selection state
   */
  updateAllViews(faceId, selected) {
    // Handle panel states when face is toggled
    this.page3.handleFaceTogglePanelStates(faceId, selected);

    this.updateFaceCard(faceId, selected);
    this.updateCanvas();
    this.updateSummaryStats();

    // Sync slider state after face toggle
    this.page3.syncSliderState();
  }

  /**
   * Update face card visual state
   * @param {number} faceId - Face identifier
   * @param {boolean} selected - Selection state
   */
  updateFaceCard(faceId, selected) {
    const faceCard = this.page3.shadowRoot.querySelector(`[data-face-id="${faceId}"]`);
    if (faceCard) {
      faceCard.classList.toggle('selected', selected);
    }
  }

  /**
   * Update canvas rendering
   */
  updateCanvas() {
    const canvas = this.page3.shadowRoot.querySelector('.roof-overlay-canvas');
    if (canvas && this.page3.roofFaces.length > 0) {
      this.page3.rerenderCanvas(canvas);
    }
  }

  /**
   * Update summary statistics
   */
  updateSummaryStats() {
    this.page3.updateSummaryStats();
  }

  /**
   * Notify observers of selection changes (for future extensibility)
   * @param {number} faceId - Face identifier
   * @param {boolean} selected - Selection state
   * @param {string} source - Source of change
   */
  notifyObservers(faceId, selected, source) {
    this.observers.forEach(observer => {
      try {
        observer({ faceId, selected, source });
      } catch (error) {
        logger.error({
          [FIELDS.ERROR]: error,
          [FIELDS.OPERATION]: 'notifyObservers',
          [FIELDS.PAGE_NUMBER]: 3
        }, 'FaceSelection observer error');
      }
    });
  }

  /**
   * Add observer for selection changes
   * @param {Function} callback - Observer callback
   */
  addObserver(callback) {
    this.observers.push(callback);
  }

  /**
   * Remove observer
   * @param {Function} callback - Observer callback to remove
   */
  removeObserver(callback) {
    const index = this.observers.indexOf(callback);
    if (index > -1) {
      this.observers.splice(index, 1);
    }
  }
}

export default class Page3 {
  constructor(widget) {
    this.widget = widget;
    this.shadowRoot = widget.shadowRoot;
    this.config = widget.config;

    // Initialize roof face management
    this.roofFaces = []; // Array of RoofFace objects
    this.facesData = []; // Array of face metadata with selection state
    this.fluxImageUrl = null; // Annual flux visualization URL
    this.fluxImage = null; // Loaded flux image (lazy loaded)

    // Initialize centralized selection management
    this.selectionManager = new FaceSelectionManager(this);
  }

  render() {
    return `
      <!-- Page 3: Modern Roof Selection Interface -->
      <div class="page" id="page-3" style="display: block;">
        <div class="page-content">
          <!-- Roof Section Header - Above Image -->
          <div class="face-grid-section">
            <div class="face-grid-header">
              <div class="property-info" id="property-info" style="display: none;">
                <div class="property-address" id="property-address">Loading address...</div>
                <div class="image-date" id="image-date">Image Date: 2024</div>
              </div>
              <h3>Select roof sections to include in analysis</h3>
            </div>
          </div>

          <!-- Image Section - Full Width -->
          <div class="image-section">
            <div class="roof-container">
              <div class="aerial-image-wrapper" id="results-images">
                <div class="loading-placeholder">
                  <p>Loading analysis results...</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Face Grid Container - Below Image -->
          <div class="face-grid-section">
            <div class="face-grid-container">
              <div class="panel-slider-container">
                <label for="panel-count-slider" class="slider-label">
                  <span>Solar Panels: <strong id="panel-count-display">0</strong></span>
                </label>
                <input
                  type="range"
                  id="panel-count-slider"
                  class="panel-slider"
                  min="0"
                  max="0"
                  value="0"
                  step="1"
                />
              </div>
              <div class="face-grid" id="face-grid">
                <!-- Face cards will be populated dynamically -->
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer - Sticky -->
        <div class="modal-footer">
          <div class="metrics-display">
            <div class="metric-item">
              <div class="metric-label">Annual Energy</div>
              <div class="metric-value" id="annual-energy">0 kWh/yr</div>
            </div>
            <div class="metric-item">
              <div class="metric-label">Installation Size</div>
              <div class="metric-value" id="installation-size">0 kW</div>
            </div>
          </div>
          <button class="quote-btn" id="quote-btn" disabled>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path>
            </svg>
            <span>Get Quote</span>
          </button>
        </div>


        <style>
          /* Page Container - Add scrollability with footer space */
          #page-3 {
            max-height: calc(90vh - 80px);
            overflow-y: auto;
            overflow-x: hidden;
            display: flex;
            flex-direction: column;
          }

          /* Main content area that can scroll */
          .page-content {
            flex: 1;
            overflow-y: auto;
            padding-bottom: 80px; /* Space for sticky footer */
          }

          /* Page Header */
          .page-header {
            padding: 0 0 24px 0;
            margin-bottom: 0;
            border-bottom: none;
          }

          .page-subtitle {
            font-size: 14px;
            color: #000000;
            line-height: 1.5;
            margin: 0;
            text-align: center;
          }


          .property-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .property-address {
            font-size: 14px;
            color: #000000;
            font-weight: 600;
          }

          .image-date {
            font-size: 12px;
            color: #000000;
          }

          .action-btn {
            padding: 10px 20px;
            background: white;
            border: 1px solid #cbd5e1;
            border-radius: 10px;
            color: #000000;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .action-btn:hover {
            background: #f1f5f9;
            border-color: #94a3b8;
            transform: translateY(-1px);
          }

          .action-btn svg {
            width: 18px;
            height: 18px;
          }

          .summary-stats {
            display: flex;
            gap: 24px;
            align-items: center;
          }

          .stat-item {
            text-align: center;
          }

          .stat-value {
            font-size: 20px;
            font-weight: 700;
            color: #000000;
          }

          .stat-value.highlight {
            color: #6366f1;
          }

          .stat-label {
            font-size: 12px;
            color: #000000;
            margin-top: 2px;
          }

          /* Image Section - Full Width */
          .image-section {
            background: transparent;
            padding-top: 16px;
            border-top: none;
          }

          /* Roof Container - 16:9 aspect ratio */
          .roof-container {
            position: relative;
            width: 100%;
            aspect-ratio: 16 / 9;
            background: white;
            overflow: hidden;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
            border-top: none;
          }


          /* Aerial Image Wrapper */
          .aerial-image-wrapper {
            position: relative;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .loading-placeholder {
            text-align: center;
            color: #000000;
            padding: 40px 0;
          }

          .image-container {
            width: 100%;
            text-align: center;
            overflow: visible;
          }

          .roof-overlay-container {
            position: relative;
            width: 100%;
            height: 100%;
            overflow: hidden;
          }

          .result-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            border-radius: 4px;
            background: white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }

          .roof-overlay-canvas {
            pointer-events: auto;
            cursor: pointer;
            position: absolute;
            top: 0;
            left: 0;
          }

          /* Face Grid Section - Below Image */
          .face-grid-section {
            background: transparent;
            display: flex;
            flex-direction: column;
          }

          .face-grid-header {
            padding: 16px 20px;
            background: transparent;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
          }

          .face-grid-header h3 {
            font-size: 16px;
            font-weight: 600;
            color: #000000;
            margin: 0;
            flex-shrink: 0;
          }

          .face-grid-container {
            padding: 0;
          }

          .panel-slider-container {
            padding: 16px 16px 8px 16px;
            background: white;
            border-bottom: 1px solid #e5e7eb;
          }

          .slider-label {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
            font-size: 14px;
            color: #374151;
          }

          .slider-label strong {
            color: #2563eb;
            font-size: 16px;
          }

          .panel-slider {
            width: 100%;
            height: 6px;
            border-radius: 3px;
            background: #e5e7eb;
            outline: none;
            -webkit-appearance: none;
            appearance: none;
          }

          .panel-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #2563eb;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .panel-slider::-webkit-slider-thumb:hover {
            background: #1d4ed8;
            transform: scale(1.1);
          }

          .panel-slider::-moz-range-thumb {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #2563eb;
            cursor: pointer;
            border: none;
            transition: all 0.2s ease;
          }

          .panel-slider::-moz-range-thumb:hover {
            background: #1d4ed8;
            transform: scale(1.1);
          }

          .face-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 12px;
            padding: 16px;
            background: transparent;
          }

          .face-card {
            background: white;
            padding: 12px;
            transition: all 0.2s ease;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            min-height: 80px;
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .face-card:hover {
            border-color: #cbd5e1;
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .face-card.selected {
            border-color: #2563eb;
            background: rgba(37, 99, 235, 0.05);
            box-shadow: 0 0 0 1px #2563eb;
          }

          .face-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
          }

          .face-name {
            font-size: 14px;
            font-weight: 600;
            color: #000000;
            flex: 1;
            text-align: left;
          }

          .face-toggle {
            background: white;
            border: 2px solid #cbd5e1;
            border-radius: 50%;
            width: 28px;
            height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 16px;
            transition: all 0.2s ease;
            flex-shrink: 0;
          }

          .face-toggle:hover {
            border-color: #2563eb;
            background: rgba(37, 99, 235, 0.05);
          }

          .face-card.selected .face-toggle {
            background: #2563eb;
            border-color: #2563eb;
            color: white;
          }

          .face-stats {
            display: flex;
            flex-direction: column;
            gap: 2px;
            font-size: 12px;
            color: #000000;
          }

          .face-error {
            color: #dc2626;
            font-weight: 500;
            font-size: 12px;
          }

          /* Modal Footer - Sticky at bottom */
          .modal-footer {
            position: sticky;
            bottom: 0;
            background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
            border-top: 2px solid #1e3a8a;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 24px;
            z-index: 10;
            box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
          }

          .metrics-display {
            display: flex;
            gap: 48px;
          }

          .metric-item {
            text-align: center;
          }

          .metric-label {
            font-size: 13px;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.8);
            margin-bottom: 6px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .metric-value {
            font-size: 24px;
            font-weight: 700;
            color: white;
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }

          .quote-btn {
            padding: 14px 32px;
            background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
            color: white;
            font-size: 16px;
            font-weight: 600;
            border: none;
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 2px 6px rgba(245, 158, 11, 0.25);
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .quote-btn:hover:not(:disabled) {
            box-shadow: 0 3px 8px rgba(245, 158, 11, 0.3);
            transform: translateY(-2px);
          }

          .quote-btn:active:not(:disabled) {
            box-shadow: 0 2px 6px rgba(245, 158, 11, 0.25);
            transform: translateY(0);
          }

          .quote-btn:disabled {
            background: rgba(255, 255, 255, 0.2);
            cursor: not-allowed;
            box-shadow: none;
            color: rgba(255, 255, 255, 0.5);
          }

          .quote-btn svg {
            width: 20px;
            height: 20px;
          }


          .no-results {
            text-align: center;
            color: #e74c3c;
            background: #fdf2f2;
            border: 1px solid #e74c3c;
            border-radius: 4px;
            padding: 20px;
            margin: 20px 0;
          }

          /* Responsive adjustments */
          @media (max-width: 768px) {
            .face-grid {
              grid-template-columns: 1fr;
            }

            .controls-row {
              flex-direction: column;
              align-items: stretch;
            }

            .summary-stats {
              justify-content: space-around;
              width: 100%;
            }

            .modal-footer {
              flex-direction: column;
              gap: 16px;
            }

            .quote-btn {
              width: 100%;
            }
          }
        </style>
      </div>
    `;
  }

  onMount(data = {}) {
    // Initialize roof face selection tracking
    this.selectedFaces = new Set();
    this.facesData = [];

    // Setup event handlers for the new UI
    this.setupEventHandlers();

    // Get job data from session
    const jobData = this.widget.getSessionData('roofScanJob');

    // Get selected address from session for property badge
    const selectedAddress = this.widget.getSessionData('selected_address');
    if (selectedAddress && selectedAddress.address) {
      this.updatePropertyBadge(selectedAddress.address);
    }

    if (jobData && jobData.jobData) {
      this.displayResults(jobData.jobData);
    } else {
      logger.error({
        [FIELDS.OPERATION]: 'onMount',
        [FIELDS.PAGE_NUMBER]: 3,
        [FIELDS.REASON]: 'no_job_data_in_session'
      }, 'No job data found in session');
      this.displayNoResults();
    }
  }

  setupEventHandlers() {
    // Select All button
    // Quote button
    const quoteBtn = this.shadowRoot.querySelector('#quote-btn');
    if (quoteBtn) {
      quoteBtn.addEventListener('click', () => this.handleQuoteRequest());
    }
  }

  updatePropertyBadge(address) {
    const propertyInfo = this.shadowRoot.querySelector('#property-info');
    const propertyAddress = this.shadowRoot.querySelector('#property-address');
    const imageDate = this.shadowRoot.querySelector('#image-date');

    if (propertyInfo && propertyAddress) {
      propertyAddress.textContent = address;

      // Set image date from session data, fallback to current year
      if (imageDate) {
        const jobData = this.widget.getSessionData('roofScanJob');
        const imageYear = jobData?.jobData?.image_date || jobData?.jobData?.imagery_date || new Date().getFullYear();
        imageDate.textContent = `Image Date: ${imageYear}`;
      }

      propertyInfo.style.display = 'flex';
    }
  }

  /**
   * Load flux visualization image from R2
   * @param {string} fluxImageUrl - URL to annual flux visualization PNG
   * @returns {Promise<void>}
   */
  async loadFluxVisualization(fluxImageUrl) {
    if (!fluxImageUrl) {
      logger.debug({
        [FIELDS.OPERATION]: 'loadFluxVisualization',
        [FIELDS.PAGE_NUMBER]: 3
      }, 'No flux visualization URL provided');
      return;
    }

    try {
      const fluxImage = new Image();
      // No crossOrigin needed - using presigned R2 URLs like RGB visualization
      fluxImage.src = fluxImageUrl;

      await new Promise((resolve, reject) => {
        fluxImage.onload = resolve;
        fluxImage.onerror = reject;
      });

      this.fluxImage = fluxImage;
      logger.info({
        [FIELDS.OPERATION]: 'loadFluxVisualization',
        [FIELDS.PAGE_NUMBER]: 3,
        url: fluxImageUrl,
        width: fluxImage.width,
        height: fluxImage.height
      }, 'Flux visualization loaded successfully');

      // Update existing RoofFace instances with the loaded flux image
      if (this.roofFaces && this.roofFaces.length > 0) {
        this.roofFaces.forEach(face => {
          face.fluxImage = fluxImage;
        });

        // Re-render canvas to show flux visualization
        const canvas = this.shadowRoot.querySelector('.roof-overlay-canvas');
        if (canvas) {
          this.rerenderCanvas(canvas);
          logger.debug({
            [FIELDS.OPERATION]: 'loadFluxVisualization',
            [FIELDS.PAGE_NUMBER]: 3
          }, 'Canvas re-rendered with flux visualization');
        }
      }

    } catch (error) {
      logger.error({
        [FIELDS.ERROR]: error,
        [FIELDS.OPERATION]: 'loadFluxVisualization',
        [FIELDS.PAGE_NUMBER]: 3,
        url: fluxImageUrl
      }, 'Failed to load flux visualization image');
      this.fluxImage = null;
    }
  }

  displayResults(jobData) {
    const resultsContainer = this.shadowRoot.querySelector('#results-images');

    if (!resultsContainer) {
      logger.error({
        [FIELDS.OPERATION]: 'displayResults',
        [FIELDS.PAGE_NUMBER]: 3,
        [FIELDS.REASON]: 'results_container_not_found'
      }, 'Results container not found');
      return;
    }

    // Store flux visualization URL for lazy loading (loaded when overlay renders)
    if (jobData.annual_flux_visualization) {
      this.fluxImageUrl = jobData.annual_flux_visualization;
      logger.debug({
        [FIELDS.OPERATION]: 'displayResults',
        [FIELDS.PAGE_NUMBER]: 3,
        fluxUrl: this.fluxImageUrl
      }, 'Flux visualization URL stored for lazy loading');
    }

    let imageHtml = '';

    // Display roof visualization with overlay if mask data is available, otherwise show plain visualization
    if (jobData.roof_outline_mask && jobData.aerial_image) {
      // Store mask data for this image to avoid HTML attribute issues
      const maskId = `mask-${Date.now()}`;
      this.maskDataStore = this.maskDataStore || {};

      // Store mask data directly (roof-server now fetches from R2 and returns arrays)
      this.maskDataStore[maskId] = {
        roofOutline: jobData.roof_outline_mask,
        roofFaces: jobData.roof_face_masks || []
      };

      const hasFaceMasks = jobData.roof_face_masks && jobData.roof_face_masks.length > 0;
      const title = hasFaceMasks ? '🎯 Roof Detection with Face Segments' : '🎯 Roof Detection with Overlay';

      imageHtml += `
        <div class="image-container">
          <div class="roof-overlay-container" style="position: relative; display: inline-block;">
            <img class="result-image base-image"
                 src="${jobData.aerial_image}"
                 alt="Roof Visualization with Overlay"
                 data-mask-id="${maskId}"
                 onload="setTimeout(() => { this.nextElementSibling.style.display='block'; this.dispatchEvent(new CustomEvent('imageLoaded', { detail: { maskId: '${maskId}' } })); }, 100)"
                 onerror="this.style.display='none'; this.parentElement.nextElementSibling.style.display='block';">
            <canvas class="roof-overlay-canvas" style="position: absolute; top: 0; left: 0; display: none;" data-mask-id="${maskId}"></canvas>
          </div>
          <div class="image-error" style="display: none;">
            Failed to load roof visualization image
          </div>
        </div>
      `;
    } else if (jobData.aerial_image) {
      // Fallback to plain visualization if no mask data
      imageHtml += `
        <div class="image-container">
          <h4>🏠 Roof Visualization</h4>
          <img class="result-image" src="${jobData.aerial_image}" alt="Roof Visualization"
               onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
          <div class="image-error" style="display: none;">
            Failed to load visualization image
          </div>
        </div>
      `;
    }

    if (imageHtml) {
      resultsContainer.innerHTML = imageHtml;

      // Set up event listeners after HTML is rendered
      this.setupImageEventListeners();
    } else {
      resultsContainer.innerHTML = '<div class="no-results">No visualization images available</div>';
    }

    // Generate face cards if we have roof face data
    if (jobData.roof_face_masks && jobData.roof_face_masks.length > 0) {
      this.generateFaceCards(jobData.roof_face_masks);
    }

  }

  setupImageEventListeners() {
    // Set up image load event listeners
    const images = this.shadowRoot.querySelectorAll('img[data-mask-id]');
    images.forEach(img => {
      img.addEventListener('imageLoaded', (event) => {
        const maskId = event.detail.maskId;
        this.renderRoofOverlay(img, maskId);
      });
    });

    // Set up canvas click event listeners
    const canvases = this.shadowRoot.querySelectorAll('canvas[data-mask-id]');
    canvases.forEach(canvas => {
      canvas.addEventListener('click', (event) => {
        this.handleRoofFaceClick(event, canvas);
      });
    });
  }

  displayAnalysisInfo(jobData, container) {
    if (!container) return;

    const processingTimeEl = container.querySelector('#processing-time');
    const uploadStatusEl = container.querySelector('#upload-status');
    const dataAvailableEl = container.querySelector('#data-available');

    // Show completion time
    if (processingTimeEl && jobData.updated_at) {
      const completedDate = new Date(jobData.updated_at);
      processingTimeEl.textContent = completedDate.toLocaleTimeString();
    } else {
      processingTimeEl.textContent = 'Completed';
    }

    // Show status based on available database fields
    if (uploadStatusEl) {
      const hasData = jobData.aerial_image || jobData.roof_outline_mask || jobData.roof_face_masks;
      const status = hasData ? '✅ Available' : '❌ No Data';
      uploadStatusEl.textContent = status;
    }

    if (dataAvailableEl) {
      const availableFields = [];
      if (jobData.aerial_image) availableFields.push('aerial_image');
      if (jobData.roof_outline_mask) availableFields.push('roof_outline_mask');
      if (jobData.roof_face_masks) availableFields.push('roof_face_masks');
      dataAvailableEl.textContent = `${availableFields.length} fields`;
    }

    container.style.display = 'block';
  }

  displayRoofSegments(jobData, container) {
    if (!container) return;

    const segmentsContainer = container.querySelector('#segments-container');
    if (!segmentsContainer) return;

    // Check if we have roof face masks
    if (!jobData.roof_face_masks || !Array.isArray(jobData.roof_face_masks)) {
      logger.error({
        [FIELDS.OPERATION]: 'displayRoofSegments',
        [FIELDS.PAGE_NUMBER]: 3,
        [FIELDS.REASON]: 'no_roof_face_masks'
      }, 'No roof face masks available for display');
      return;
    }

    const masks = jobData.roof_face_masks;

    let segmentsHtml = '';

    masks.forEach((mask, index) => {
      const confidence = mask.sam_confidence ? (mask.sam_confidence * 100).toFixed(1) : 'N/A';
      const area = mask.area ? Math.round(mask.area) : 'N/A';
      const color = '#ff0000'; // All segments use red color to match overlay

      segmentsHtml += `
        <div class="segment-item">
          <div class="segment-info">
            <div style="display: flex; align-items: center; gap: 8px;">
              <div style="width: 16px; height: 16px; background: ${color}; border-radius: 3px; flex-shrink: 0;"></div>
              <span><strong>Segment ${index + 1}</strong></span>
            </div>
            <span>Confidence: <span class="segment-confidence">${confidence}%</span></span>
            <span>Area: ${area} px²</span>
          </div>
        </div>
      `;
    });

    if (segmentsHtml) {
      segmentsContainer.innerHTML = segmentsHtml;
      container.style.display = 'block';
    }
  }

  displayNoResults() {
    const resultsContainer = this.shadowRoot.querySelector('#results-images');
    if (resultsContainer) {
      resultsContainer.innerHTML = `
        <div class="no-results">
          <h4>⚠️ No Results Available</h4>
          <p>Analysis completed but no result data was found.</p>
          <p>This may indicate an issue with the processing or data storage.</p>
        </div>
      `;
    }
  }

  renderRoofOverlay(imageElement, maskId, retryCount = 0) {
    try {
      // Load flux visualization if URL is available and not yet loaded
      if (this.fluxImageUrl && !this.fluxImage) {
        this.loadFluxVisualization(this.fluxImageUrl);
        // Note: flux image will be available on next render if needed
      }

      // Retrieve mask data from store using ID
      const storedData = this.maskDataStore && this.maskDataStore[maskId];
      if (!storedData) {
        logger.error({
          [FIELDS.OPERATION]: 'renderRoofOverlay',
          [FIELDS.PAGE_NUMBER]: 3,
          [FIELDS.REASON]: 'mask_data_not_found'
        }, `No mask data found for ID: ${maskId}`);
        return;
      }

      // Use mask data directly from roof-server (no fetching needed)
      const roofOutlineData = storedData.roofOutline;
      const roofFacesData = storedData.roofFaces;

      const canvas = imageElement.nextElementSibling;
      if (!canvas || canvas.tagName !== 'CANVAS') {
        logger.error({
          [FIELDS.OPERATION]: 'renderRoofOverlay',
          [FIELDS.PAGE_NUMBER]: 3,
          [FIELDS.REASON]: 'canvas_not_found'
        }, 'Canvas element not found for roof overlay');
        return;
      }

      const ctx = canvas.getContext('2d');

      // Ensure image has rendered and get its actual dimensions
      const rect = imageElement.getBoundingClientRect();

      if (rect.width === 0 || rect.height === 0) {
        if (retryCount >= 10) {
          logger.error({
            [FIELDS.OPERATION]: 'renderRoofOverlay',
            [FIELDS.PAGE_NUMBER]: 3,
            [FIELDS.REASON]: 'image_zero_dimensions_max_retries',
            retryCount: retryCount
          }, 'Image has zero dimensions after max retries');
          return;
        }
        setTimeout(() => this.renderRoofOverlay(imageElement, maskId, retryCount + 1), 200);
        return;
      }

      // Set canvas size to match displayed image
      canvas.width = rect.width;
      canvas.height = rect.height;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let totalPixelsRendered = 0;

      // Create RoofFace objects and render them
      if (roofFacesData && roofFacesData.length > 0) {
        // Debug: Log first face mask structure
        logger.info({
          [FIELDS.OPERATION]: 'renderRoofOverlay',
          [FIELDS.PAGE_NUMBER]: 3,
          faceCount: roofFacesData.length,
          firstFaceFields: Object.keys(roofFacesData[0]),
          hasMaskArray: !!roofFacesData[0].mask_array,
          hasMaskDimensions: !!roofFacesData[0].mask_dimensions,
          maskArrayType: typeof roofFacesData[0].mask_array,
          maskArrayLength: roofFacesData[0].mask_array?.length,
          maskDimensions: roofFacesData[0].mask_dimensions
        }, 'Roof face data received in plugin');

        // Clear existing roof faces and create new ones
        this.roofFaces = [];
        const scaleX = canvas.width / roofFacesData[0].mask_dimensions[0];
        const scaleY = canvas.height / roofFacesData[0].mask_dimensions[1];

        roofFacesData.forEach((faceMask, index) => {
          if (faceMask.mask_array && faceMask.mask_dimensions) {
            // Create RoofFace object with solar panel data from facesData and flux image
            const solarPanels = this.facesData[index]?.solarPanels || null;

            // Debug log solar panel data for this face
            logger.debug({
              operation: 'createRoofFace',
              faceIndex: index,
              hasSolarPanels: !!solarPanels,
              panelCount: solarPanels?.count || 0,
              hasFluxImage: !!this.fluxImage,
              solarPanelsSource: solarPanels ? 'facesData' : 'none'
            }, `Creating RoofFace ${index} with solar panels`);

            const roofFace = new RoofFace(faceMask.mask_array, faceMask.mask_dimensions, index, solarPanels, this.fluxImage);
            this.roofFaces.push(roofFace);

            // Render the face using its own render method (initially unselected)
            const isSelected = this.facesData[index]?.selected || false;
            const pixels = roofFace.render(ctx, scaleX, scaleY, isSelected);
            totalPixelsRendered += pixels;
          } else {
            logger.error({
              [FIELDS.OPERATION]: 'renderRoofOverlay',
              [FIELDS.PAGE_NUMBER]: 3,
              [FIELDS.REASON]: 'missing_face_mask_data',
              faceIndex: index + 1
            }, `Face ${index + 1}: no mask_array or mask_dimensions available`);
          }
        });
      } else {
        logger.error({
          [FIELDS.OPERATION]: 'renderRoofOverlay',
          [FIELDS.PAGE_NUMBER]: 3,
          [FIELDS.REASON]: 'no_roof_face_masks_available'
        }, 'No roof face masks available to render');
        this.roofFaces = []; // Clear faces if none available
      }

      // Ensure canvas is visible and interactive
      canvas.style.display = 'block';

      // Clean up mask data to prevent memory leaks
      if (this.maskDataStore && this.maskDataStore[maskId]) {
        delete this.maskDataStore[maskId];
      }

    } catch (error) {
      logger.error({
        [FIELDS.ERROR]: error,
        [FIELDS.OPERATION]: 'renderRoofOverlay',
        [FIELDS.PAGE_NUMBER]: 3
      }, 'Error rendering roof overlay');
    }
  }

  renderMaskLayer(ctx, maskArray, maskDimensions, color, canvasWidth, canvasHeight, layerName) {
    if (!maskArray || !maskDimensions) {
      logger.error({
        [FIELDS.OPERATION]: 'renderMaskLayer',
        [FIELDS.PAGE_NUMBER]: 3,
        [FIELDS.REASON]: 'invalid_mask_data',
        layerName: layerName
      }, `Invalid ${layerName} mask data - missing array or dimensions`);
      return 0;
    }

    const [maskWidth, maskHeight] = maskDimensions;
    const scaleX = canvasWidth / maskWidth;
    const scaleY = canvasHeight / maskHeight;
    const pixelSize = Math.max(1, Math.min(scaleX, scaleY));

    ctx.fillStyle = color;
    let pixelsRendered = 0;

    // Render mask pixels efficiently
    for (let i = 0; i < maskArray.length; i++) {
      if (maskArray[i] > 0) {
        const x = (i % maskWidth) * scaleX;
        const y = Math.floor(i / maskWidth) * scaleY;
        ctx.fillRect(x, y, pixelSize, pixelSize);
        pixelsRendered++;
      }
    }

    return pixelsRendered;
  }

  /**
   * Find panel at a specific point in mask coordinates
   * @param {number} faceIndex - Roof face index
   * @param {number} maskX - X coordinate in mask space
   * @param {number} maskY - Y coordinate in mask space
   * @returns {number} Panel index or -1 if not found
   */
  findPanelAtPoint(faceIndex, maskX, maskY) {
    const face = this.facesData[faceIndex];
    if (!face?.solarPanels?.panels) return -1;

    return face.solarPanels.panels.findIndex(panel => {
      return maskX >= panel.x && maskX <= panel.x + panel.width &&
             maskY >= panel.y && maskY <= panel.y + panel.height;
    });
  }

  /**
   * Toggle individual panel enabled state
   * @param {number} faceIndex - Roof face index
   * @param {number} panelIndex - Panel index within the face
   */
  togglePanelEnabled(faceIndex, panelIndex) {
    const face = this.facesData[faceIndex];
    if (!face?.solarPanels?.panels?.[panelIndex]) return;

    const panel = face.solarPanels.panels[panelIndex];
    panel.enabled = !panel.enabled;

    logger.info({
      [FIELDS.OPERATION]: 'togglePanelEnabled',
      [FIELDS.PAGE_NUMBER]: 3,
      faceIndex,
      panelIndex,
      newState: panel.enabled
    }, 'Panel toggled');

    // Recalculate enabled panel counts and flux
    this.recalculateFaceStats(faceIndex);

    // Update RoofFace instance panel data
    if (this.roofFaces[faceIndex]) {
      this.roofFaces[faceIndex].solarPanels.panels[panelIndex].enabled = panel.enabled;
    }

    // Re-render canvas
    const canvas = this.shadowRoot.querySelector('.roof-overlay-canvas');
    if (canvas) {
      this.rerenderCanvas(canvas);
    }

    // Update face card display
    this.updateFaceCard(faceIndex);

    // Sync slider value to reflect manual panel toggle
    this.syncSliderState();
  }

  /**
   * Recalculate enabled panel count and flux for a face
   * @param {number} faceIndex - Roof face index
   */
  recalculateFaceStats(faceIndex) {
    const face = this.facesData[faceIndex];
    if (!face?.solarPanels?.panels) return;

    const enabledPanels = face.solarPanels.panels.filter(p => p.enabled !== false);
    face.enabledPanelCount = enabledPanels.length;

    // Calculate flux only for enabled panels
    face.enabledAnnualFlux = enabledPanels.reduce(
      (sum, panel) => sum + (panel.annualFlux || 0),
      0
    );

    logger.debug({
      faceIndex,
      enabledPanelCount: face.enabledPanelCount,
      totalPanelCount: face.panelCount,
      enabledAnnualFlux: face.enabledAnnualFlux
    }, 'Face stats recalculated');
  }

  /**
   * Update face card HTML with current stats
   * @param {number} faceIndex - Roof face index
   */
  updateFaceCard(faceIndex) {
    const face = this.facesData[faceIndex];
    const faceCard = this.shadowRoot.querySelector(`[data-face-id="${faceIndex}"]`);

    if (!faceCard || !face) return;

    // Update card selected class
    if (face.selected) {
      faceCard.classList.add('selected');
    } else {
      faceCard.classList.remove('selected');
    }

    // Update toggle button
    const toggleButton = faceCard.querySelector('.face-toggle');
    if (toggleButton) {
      toggleButton.textContent = face.selected ? '✓' : '○';
    }

    // Update stats
    const statsHtml = face.hasDsmData ? `
      <span>Pitch: ${face.pitch?.toFixed(0)}°</span>
      <span>Size: ${(face.actualArea || face.projectedArea)?.toFixed(0)} m²</span>
      ${face.panelCount > 0 ? `<span>Panels: ${face.enabledPanelCount}/${face.panelCount}</span>` : ''}
      ${face.enabledAnnualFlux > 0 ? `<span>Solar: ${(face.enabledAnnualFlux * 0.4 / 1000).toFixed(1)}k kWh/yr</span>` : ''}
    ` : `
      <span>No roof data</span>
    `;

    const statsContainer = faceCard.querySelector('.face-stats');
    if (statsContainer) {
      statsContainer.innerHTML = statsHtml;
    }
  }

  handleRoofFaceClick(event, canvas) {
    try {
      if (!this.roofFaces || this.roofFaces.length === 0) {
        logger.error({
          [FIELDS.OPERATION]: 'handleRoofFaceClick',
          [FIELDS.PAGE_NUMBER]: 3,
          [FIELDS.REASON]: 'no_roof_faces_available'
        }, 'User clicked canvas but no roof faces are available');
        return;
      }

      // Get click coordinates relative to canvas
      const rect = canvas.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const clickY = event.clientY - rect.top;

      // Convert canvas coordinates to mask coordinates
      const firstFace = this.roofFaces[0];
      const scaleX = firstFace.dimensions[0] / canvas.width;
      const scaleY = firstFace.dimensions[1] / canvas.height;

      const maskX = Math.floor(clickX * scaleX);
      const maskY = Math.floor(clickY * scaleY);

      // Find which face contains this point
      let clickedFace = null;
      for (let i = this.roofFaces.length - 1; i >= 0; i--) { // Check from top layer down
        const face = this.roofFaces[i];
        if (face.containsPoint(maskX, maskY)) {
          clickedFace = face;
          break;
        }
      }

      if (clickedFace) {
        const faceIndex = clickedFace.index;
        const faceEnabled = this.facesData[faceIndex]?.selected || false;

        // If face is enabled, check for panel click
        if (faceEnabled) {
          const panelIndex = this.findPanelAtPoint(faceIndex, maskX, maskY);

          if (panelIndex !== -1) {
            // Click on individual panel - toggle panel
            logger.info({
              [FIELDS.OPERATION]: 'handleRoofFaceClick',
              [FIELDS.PAGE_NUMBER]: 3,
              action: 'toggle_panel',
              faceIndex,
              panelIndex
            }, 'Panel click detected');

            this.togglePanelEnabled(faceIndex, panelIndex);
            return;
          }
        }

        // Clicking on face area (not panel) does nothing now
        // Face selection is controlled only by toggle buttons in cards
      }

    } catch (error) {
      logger.error({
        [FIELDS.ERROR]: error,
        [FIELDS.OPERATION]: 'handleRoofFaceClick',
        [FIELDS.PAGE_NUMBER]: 3
      }, 'Error handling roof face click');
    }
  }

  rerenderCanvas(canvas) {
    try {
      const ctx = canvas.getContext('2d');

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Get scale factors from first face
      if (this.roofFaces.length > 0) {
        const firstFace = this.roofFaces[0];
        const scaleX = canvas.width / firstFace.dimensions[0];
        const scaleY = canvas.height / firstFace.dimensions[1];

        // Re-render all roof faces with their current states from single source of truth
        this.roofFaces.forEach((face, index) => {
          const isSelected = this.facesData[index]?.selected || false;
          face.render(ctx, scaleX, scaleY, isSelected);
        });
      }
    } catch (error) {
      logger.error({
        [FIELDS.ERROR]: error,
        [FIELDS.OPERATION]: 'rerenderCanvas',
        [FIELDS.PAGE_NUMBER]: 3
      }, 'Error re-rendering canvas');
    }
  }

  // Get currently selected roof faces for external processing
  getSelectedRoofFaces() {
    if (!this.roofFaces || this.roofFaces.length === 0) {
      return [];
    }

    // Use selection manager to get selected faces from single source of truth
    const selectedFacesData = this.selectionManager.getSelectedFaces();

    return selectedFacesData.map(faceData => {
      const roofFace = this.roofFaces[faceData.id];
      return {
        index: roofFace.index,
        pixelCount: roofFace.activePixelCount,
        boundingBox: roofFace.boundingBox,
        maskArray: roofFace.maskArray,
        dimensions: roofFace.dimensions
      };
    });
  }

  // Get all roof faces with their current states
  getAllRoofFaceStates() {
    if (!this.roofFaces || this.roofFaces.length === 0) {
      return [];
    }

    return this.roofFaces.map((face, index) => {
      const faceState = face.getState();
      const isSelected = this.facesData[index]?.selected || false;
      return {
        ...faceState,
        isSelected
      };
    });
  }

  generateFaceCards(roofFaceMasks) {
    const faceGrid = this.shadowRoot.querySelector('#face-grid');
    if (!faceGrid) {
      logger.error({
        [FIELDS.OPERATION]: 'generateFaceCards',
        [FIELDS.PAGE_NUMBER]: 3,
        [FIELDS.REASON]: 'face_grid_not_found'
      }, 'Face grid container not found');
      return;
    }

    // Store faces data for tracking selection
    this.facesData = roofFaceMasks.map((mask, index) => {
      // Create directional name based on compass direction
      const direction = mask.dsm_analysis?.roof_pitch?.direction_azimuth ?
        this.getCompassDirection(mask.dsm_analysis.roof_pitch.direction_azimuth) :
        '';
      const name = direction ? `${index + 1}. ${direction} Roof` : `${index + 1}. Roof`;

      return {
        id: index,
        name: name,
        area: mask.area ? Math.round(mask.area) : 0,
        confidence: mask.sam_confidence ? (mask.sam_confidence * 100).toFixed(1) : 0,
        color: this.getFaceColor(index),
        selected: true, // Initially all faces selected

        // DSM Analysis data (safe with optional chaining)
        pitch: mask.dsm_analysis?.roof_pitch?.angle_degrees,
        azimuth: mask.dsm_analysis?.roof_pitch?.direction_azimuth,
        actualArea: mask.dsm_analysis?.surface_area?.actual_area_m2,
        projectedArea: mask.dsm_analysis?.surface_area?.projected_area_m2,
        hasDsmData: !!mask.dsm_analysis && !mask.dsm_analysis.error,
        dsmError: mask.dsm_analysis?.error,

        // Solar panel placement data with enabled state
        solarPanels: mask.solar_panels ? {
          ...mask.solar_panels,
          panels: mask.solar_panels.panels.map(panel => ({
            ...panel,
            enabled: true  // Default all panels enabled
          }))
        } : null,
        panelCount: mask.solar_panels?.count || 0,
        enabledPanelCount: mask.solar_panels?.count || 0,  // Initially all enabled
        totalAnnualFlux: mask.solar_panels?.totalAnnualFlux || 0,
        enabledAnnualFlux: mask.solar_panels?.totalAnnualFlux || 0,  // Initially all enabled
        priority: mask.solar_panels?.priority || null,  // Priority ranking from server
        averagePanelFlux: mask.solar_panels?.averagePanelFlux || 0  // Average flux per panel
      };
    });

    // Sort faces by priority (1 = best, 2 = second, etc.)
    // Faces without panels (priority = null) go to the end
    this.facesData.sort((a, b) => {
      if (a.priority === null && b.priority === null) return a.id - b.id;
      if (a.priority === null) return 1;  // No panels go to end
      if (b.priority === null) return -1; // No panels go to end
      return a.priority - b.priority;  // Lower priority number = better (1, 2, 3...)
    });

    // Debug log solar panel data
    logger.info({
      operation: 'facesDataMapping',
      pageNumber: 3,
      totalFaces: this.facesData.length,
      solarPanelCounts: this.facesData.map(f => f.panelCount),
      totalAnnualFluxValues: this.facesData.map(f => f.totalAnnualFlux),
      firstFaceSolarPanels: this.facesData[0]?.solarPanels,
      firstFacePanelCount: this.facesData[0]?.panelCount,
      firstFaceTotalFlux: this.facesData[0]?.totalAnnualFlux
    }, 'Solar panel data mapped to facesData');

    // Generate face cards HTML as minimal cards
    const faceCardsHtml = this.facesData.map(face => `
      <div class="face-card ${face.selected ? 'selected' : ''}" data-face-id="${face.id}">
        <div class="face-header">
          <div class="face-name">${face.name}</div>
          <button class="face-toggle" data-face-id="${face.id}" aria-label="Toggle roof face">
            ${face.selected ? '✓' : '○'}
          </button>
        </div>
        <div class="face-stats">
          ${face.hasDsmData ? `
            <span>Pitch: ${face.pitch?.toFixed(0)}°</span>
            <span>Size: ${(face.actualArea || face.projectedArea)?.toFixed(0)} m²</span>
            ${face.panelCount > 0 ? `<span>Panels: ${face.enabledPanelCount}/${face.panelCount}</span>` : ''}
            ${face.enabledAnnualFlux > 0 ? `<span>Solar: ${(face.enabledAnnualFlux * 0.4 / 1000).toFixed(1)}k kWh/yr</span>` : ''}
          ` : `
            <span>No roof data</span>
          `}
        </div>
      </div>
    `).join('');

    faceGrid.innerHTML = faceCardsHtml;

    // Add click event listeners for toggle buttons only
    faceGrid.querySelectorAll('.face-toggle').forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent card click
        const faceId = parseInt(button.dataset.faceId);
        this.toggleFaceSelection(faceId);
      });
    });

    // Initialize panel slider
    this.initializePanelSlider();

    // Update summary stats
    this.updateSummaryStats();

    // Log error for any faces with DSM processing errors
    const withErrors = this.facesData.filter(face => face.dsmError);
    if (withErrors.length > 0) {
      logger.error({
        [FIELDS.OPERATION]: 'generateFaceCards',
        [FIELDS.PAGE_NUMBER]: 3,
        [FIELDS.REASON]: 'dsm_analysis_errors',
        faceCount: withErrors.length,
        totalFaces: this.facesData.length,
        errors: withErrors.map(f => ({ id: f.id, error: f.dsmError }))
      }, `${withErrors.length} face(s) failed DSM analysis`);
    }

    // Log error for any faces without DSM data
    const withoutDsm = this.facesData.filter(face => !face.hasDsmData && !face.dsmError);
    if (withoutDsm.length > 0) {
      logger.error({
        [FIELDS.OPERATION]: 'generateFaceCards',
        [FIELDS.PAGE_NUMBER]: 3,
        [FIELDS.REASON]: 'faces_without_dsm_data',
        faceCount: withoutDsm.length,
        totalFaces: this.facesData.length
      }, `${withoutDsm.length} face(s) have no DSM data available`);
    }
  }

  /**
   * Initialize the panel count slider
   */
  initializePanelSlider() {
    const slider = this.shadowRoot.querySelector('#panel-count-slider');
    const display = this.shadowRoot.querySelector('#panel-count-display');

    if (!slider || !display) return;

    // Calculate total panels across all faces
    const totalPanels = this.facesData.reduce((sum, face) => sum + (face.panelCount || 0), 0);

    // Set initial value to 10 panels or max available (whichever is less)
    const initialPanelCount = Math.min(10, totalPanels);

    // Set slider range
    slider.max = totalPanels;
    slider.value = initialPanelCount;
    display.textContent = initialPanelCount;

    // Build smart panel order (priority + adjacency)
    this.panelEnableOrder = this.buildPanelEnableOrder();

    // Set initial panel states to match slider value
    this.updatePanelStates(initialPanelCount);

    // Add slider event listener
    slider.addEventListener('input', (e) => {
      const targetCount = parseInt(e.target.value);
      display.textContent = targetCount;
      this.updatePanelStates(targetCount);
    });

    logger.info({
      [FIELDS.OPERATION]: 'initializePanelSlider',
      [FIELDS.PAGE_NUMBER]: 3,
      totalPanels,
      orderLength: this.panelEnableOrder.length
    }, 'Panel slider initialized');
  }

  /**
   * Build smart panel enable order based on priority and adjacency
   * Only includes panels from enabled (selected) faces
   * @returns {Array} Ordered list of {faceIndex, panelIndex} objects
   */
  buildPanelEnableOrder() {
    const order = [];

    // Filter to only enabled faces with panels (sorted by priority in facesData)
    const facesWithPanels = this.facesData.filter(f =>
      f.selected && f.solarPanels && f.panelCount > 0
    );

    for (const face of facesWithPanels) {
      const faceIndex = face.id;
      const panels = face.solarPanels.panels;

      if (!panels || panels.length === 0) continue;

      // Build adjacency graph for this face
      const adjacencyGraph = this.buildAdjacencyGraph(panels);

      // Find starting panel (first panel by default, or center-most)
      const enabledPanels = new Set();
      const panelOrder = [];

      // Add first panel
      panelOrder.push(0);
      enabledPanels.add(0);

      // Add remaining panels based on adjacency
      while (panelOrder.length < panels.length) {
        let bestPanel = null;
        let bestAdjacencyCount = 0;

        // Find panel with most adjacencies to already-enabled panels
        for (let i = 0; i < panels.length; i++) {
          if (enabledPanels.has(i)) continue;

          const adjacencyCount = adjacencyGraph[i].filter(adj => enabledPanels.has(adj)).length;

          if (adjacencyCount > bestAdjacencyCount) {
            bestAdjacencyCount = adjacencyCount;
            bestPanel = i;
          }
        }

        // If no adjacent panel found, pick first non-enabled
        if (bestPanel === null) {
          for (let i = 0; i < panels.length; i++) {
            if (!enabledPanels.has(i)) {
              bestPanel = i;
              break;
            }
          }
        }

        panelOrder.push(bestPanel);
        enabledPanels.add(bestPanel);
      }

      // Add to overall order
      for (const panelIndex of panelOrder) {
        order.push({ faceIndex, panelIndex });
      }
    }

    return order;
  }

  /**
   * Build adjacency graph for panels (which panels touch each other)
   * @param {Array} panels - Array of panel objects with x, y, width, height
   * @returns {Array} Array of arrays - adjacencyGraph[i] = [list of adjacent panel indices]
   */
  buildAdjacencyGraph(panels) {
    const graph = panels.map(() => []);
    const ADJACENCY_THRESHOLD = 2; // pixels

    for (let i = 0; i < panels.length; i++) {
      const p1 = panels[i];
      for (let j = i + 1; j < panels.length; j++) {
        const p2 = panels[j];

        // Check if panels are adjacent (touching or very close)
        const horizontallyAdjacent =
          Math.abs(p1.x + p1.width - p2.x) <= ADJACENCY_THRESHOLD || // p1 left of p2
          Math.abs(p2.x + p2.width - p1.x) <= ADJACENCY_THRESHOLD;   // p2 left of p1

        const verticallyAdjacent =
          Math.abs(p1.y + p1.height - p2.y) <= ADJACENCY_THRESHOLD || // p1 above p2
          Math.abs(p2.y + p2.height - p1.y) <= ADJACENCY_THRESHOLD;   // p2 above p1

        const horizontalOverlap =
          (p1.x < p2.x + p2.width && p1.x + p1.width > p2.x);

        const verticalOverlap =
          (p1.y < p2.y + p2.height && p1.y + p1.height > p2.y);

        // Panels are adjacent if they touch on one axis and overlap on the other
        if ((horizontallyAdjacent && verticalOverlap) || (verticallyAdjacent && horizontalOverlap)) {
          graph[i].push(j);
          graph[j].push(i);
        }
      }
    }

    return graph;
  }

  /**
   * Update panel enabled states based on slider value
   * @param {number} targetCount - Number of panels that should be enabled
   */
  updatePanelStates(targetCount) {
    // Disable all panels first
    for (const face of this.facesData) {
      if (face.solarPanels && face.solarPanels.panels) {
        for (const panel of face.solarPanels.panels) {
          panel.enabled = false;
        }
      }
    }

    // Rebuild order to respect current face selection states
    const currentOrder = this.buildPanelEnableOrder();

    // Enable panels according to the smart order (filtered by enabled faces)
    for (let i = 0; i < Math.min(targetCount, currentOrder.length); i++) {
      const { faceIndex, panelIndex } = currentOrder[i];
      const face = this.facesData[faceIndex];

      if (face && face.solarPanels && face.solarPanels.panels[panelIndex]) {
        face.solarPanels.panels[panelIndex].enabled = true;
      }
    }

    // Recalculate stats for all faces
    for (let faceIndex = 0; faceIndex < this.facesData.length; faceIndex++) {
      this.recalculateFaceStats(faceIndex);
      this.updateFaceCard(faceIndex);

      // Update RoofFace instance
      if (this.roofFaces[faceIndex] && this.facesData[faceIndex].solarPanels) {
        this.roofFaces[faceIndex].solarPanels = this.facesData[faceIndex].solarPanels;
      }
    }

    // Re-render canvas
    const canvas = this.shadowRoot.querySelector('.roof-overlay-canvas');
    if (canvas) {
      this.rerenderCanvas(canvas);
    }

    // Update summary and metrics
    this.updateSummaryStats();
    this.updateSystemMetrics();
  }

  /**
   * Handle panel state changes when a face is toggled
   * @param {number} faceId - Face identifier
   * @param {boolean} selected - New selection state
   */
  handleFaceTogglePanelStates(faceId, selected) {
    const face = this.facesData[faceId];
    if (!face || !face.solarPanels || !face.solarPanels.panels) return;

    // If face is being disabled, disable all its panels
    if (!selected) {
      for (const panel of face.solarPanels.panels) {
        panel.enabled = false;
      }
      this.recalculateFaceStats(faceId);
    }
    // If face is being enabled, don't auto-enable panels (let slider control it)
  }

  /**
   * Sync slider state to match current panel and face states
   * Updates both slider value and max based on enabled faces
   */
  syncSliderState() {
    const slider = this.shadowRoot.querySelector('#panel-count-slider');
    const display = this.shadowRoot.querySelector('#panel-count-display');

    if (!slider || !display) return;

    // Calculate max panels from enabled faces only
    const maxPanels = this.facesData
      .filter(f => f.selected)
      .reduce((sum, face) => sum + (face.panelCount || 0), 0);

    // Calculate currently enabled panels
    const currentEnabledCount = this.facesData.reduce((sum, face) => {
      if (!face.solarPanels || !face.solarPanels.panels) return sum;
      return sum + face.solarPanels.panels.filter(p => p.enabled).length;
    }, 0);

    // Update slider
    slider.max = maxPanels;
    slider.value = currentEnabledCount;
    display.textContent = currentEnabledCount;

    // Update system metrics
    this.updateSystemMetrics();

    logger.debug({
      [FIELDS.OPERATION]: 'syncSliderState',
      [FIELDS.PAGE_NUMBER]: 3,
      maxPanels,
      currentEnabledCount
    }, 'Slider state synchronized');
  }

  /**
   * Update system metrics display (Annual Energy and Installation Size)
   */
  updateSystemMetrics() {
    const annualEnergyEl = this.shadowRoot.querySelector('#annual-energy');
    const installationSizeEl = this.shadowRoot.querySelector('#installation-size');

    if (!annualEnergyEl || !installationSizeEl) return;

    // Calculate total enabled annual flux across all faces
    const totalEnabledFlux = this.facesData.reduce((sum, face) => {
      return sum + (face.enabledAnnualFlux || 0);
    }, 0);

    // Calculate total energy (flux × 0.4kW panel capacity)
    const annualEnergy = totalEnabledFlux * 0.4; // kWh/year

    // Calculate installation size (enabled panel count × 0.4kW)
    const enabledPanelCount = this.facesData.reduce((sum, face) => {
      if (!face.solarPanels || !face.solarPanels.panels) return sum;
      return sum + face.solarPanels.panels.filter(p => p.enabled).length;
    }, 0);
    const installationSize = enabledPanelCount * 0.4; // kW

    // Update display
    annualEnergyEl.textContent = `${(annualEnergy / 1000).toFixed(1)}k kWh/yr`;
    installationSizeEl.textContent = `${installationSize.toFixed(1)} kW`;

    logger.debug({
      [FIELDS.OPERATION]: 'updateSystemMetrics',
      [FIELDS.PAGE_NUMBER]: 3,
      annualEnergy: annualEnergy.toFixed(0),
      installationSize: installationSize.toFixed(1),
      enabledPanelCount
    }, 'System metrics updated');
  }

  getFaceColor(index) {
    const colors = ['#3b82f6', '#22c55e', '#a855f7', '#fb923c', '#ef4444', '#06b6d4'];
    return colors[index % colors.length];
  }

  /**
   * Convert azimuth degrees to compass direction
   * Ground truth: 0° = West, 90° = North, 180° = East, 270° = South
   * @param {number} azimuth - Azimuth in degrees (0-360)
   * @returns {string} Full compass direction name (North, Northeast, East, etc.)
   */
  getCompassDirection(azimuth) {
    if (azimuth == null || isNaN(azimuth)) return '';

    // Normalize to 0-360 range
    const normalizedAzimuth = ((azimuth % 360) + 360) % 360;

    // Define 8 compass directions (45° each)
    // 0° = West, 90° = North, 180° = East, 270° = South
    if (normalizedAzimuth >= 337.5 || normalizedAzimuth < 22.5) return 'West';
    if (normalizedAzimuth >= 22.5 && normalizedAzimuth < 67.5) return 'Northwest';
    if (normalizedAzimuth >= 67.5 && normalizedAzimuth < 112.5) return 'North';
    if (normalizedAzimuth >= 112.5 && normalizedAzimuth < 157.5) return 'Northeast';
    if (normalizedAzimuth >= 157.5 && normalizedAzimuth < 202.5) return 'East';
    if (normalizedAzimuth >= 202.5 && normalizedAzimuth < 247.5) return 'Southeast';
    if (normalizedAzimuth >= 247.5 && normalizedAzimuth < 292.5) return 'South';
    if (normalizedAzimuth >= 292.5 && normalizedAzimuth < 337.5) return 'Southwest';

    return ''; // Fallback to empty string
  }

  toggleFaceSelection(faceId) {
    // Use centralized selection manager
    this.selectionManager.toggle(faceId, 'faceCard');
  }

  // updateAllFaceCards() method removed - now handled by FaceSelectionManager
  // selectAllFaces() and clearAllFaces() removed - buttons removed from UI

  updateSummaryStats() {
    const selectedFaces = this.facesData.filter(face => face.selected);

    // Calculate total area - only include faces with DSM data (in m²)
    const totalArea = selectedFaces.reduce((sum, face) => {
      if (face.hasDsmData && face.actualArea) {
        return sum + face.actualArea;
      } else if (face.hasDsmData && face.projectedArea) {
        return sum + face.projectedArea;
      } else {
        // Skip faces without DSM data (don't add pixel values to m² total)
        return sum;
      }
    }, 0);

    // Always use m² units
    const units = 'm²';

    // Update face count
    const faceCountEl = this.shadowRoot.querySelector('#face-count');
    if (faceCountEl) {
      faceCountEl.textContent = `${selectedFaces.length} of ${this.facesData.length}`;
    }

    // Update total area with m² units
    const totalAreaEl = this.shadowRoot.querySelector('#total-area');
    if (totalAreaEl) {
      totalAreaEl.textContent = `${totalArea.toFixed(1)} ${units}`;
    }

    // Update quote button state
    const quoteBtn = this.shadowRoot.querySelector('#quote-btn');
    if (quoteBtn) {
      quoteBtn.disabled = selectedFaces.length === 0;
    }
  }

  handleQuoteRequest() {
    const selectedFaces = this.facesData.filter(face => face.selected);
    if (selectedFaces.length === 0) {
      logger.error({
        [FIELDS.OPERATION]: 'handleQuoteRequest',
        [FIELDS.PAGE_NUMBER]: 3,
        [FIELDS.REASON]: 'no_faces_selected'
      }, 'User attempted quote request with no faces selected');
      return;
    }

    // Store selected roof faces data in session for Page4
    this.widget.setSessionData('selected_roof_faces', {
      faces: selectedFaces,
      totalArea: selectedFaces.reduce((sum, face) => sum + (face.actualArea || face.area || 0), 0),
      timestamp: new Date().toISOString()
    });

    // Transition to Page4
    this.widget.showPage(4);
  }

  onUnmount() {
    // Clean up mask data store
    if (this.maskDataStore) {
      this.maskDataStore = {};
    }

    // Clear roof faces and face data
    this.roofFaces = [];
    this.facesData = [];
    // Note: Selection state is automatically cleared when facesData is cleared
  }
}